import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Building2, Crosshair, ArrowUpRight } from "lucide-react";
import { DossierLabel } from "../dossier/DossierLabel";
import { KeyPlayersSection } from "./DossierSections";
import { RecordThumb } from "../media/PublishedMedia";
import { useTopicEntities } from "../../hooks/use-topic-entities";
import { EntityImageFallback } from "../EntityImageFallback";

const GROUP_PATH = { person: "/people", operation: "/operations" };
export const pathForEntity = (e) => `${GROUP_PATH[e.kind_group] || "/organizations"}/${e.slug}`;
const pathFor = pathForEntity;

export const KIND_LABEL = {
  government_agency: "Government Agency", company: "Company",
  group_or_network: "Group / Network", law_or_policy: "Law / Policy",
  court_case: "Court Case", program: "Program", event: "Event",
  place: "Place", document: "Document", person: "Person",
  organization: "Organization", operation: "Operation",
};

/** Mini intelligence card — appears on hover / tap of an entity chip.
 * Span-based markup so it can legally render inside prose <p> elements. */
export const MiniCard = ({ e }) => (
  <span
    className="absolute z-30 left-0 bottom-full mb-2 w-[300px] border shadow-2xl p-4 block"
    style={{ borderColor: "#3A3630", background: "#141317" }}
    data-testid={`entity-mini-card-${e.slug}`}
  >
    <span className="flex gap-3">
      {e.portrait_url ? (
        <img
          src={/^https?:\/\//i.test(e.portrait_url) ? e.portrait_url : `${process.env.REACT_APP_BACKEND_URL}${e.portrait_url}`}
          alt={e.name}
          className="w-16 h-20 object-cover shrink-0"
          style={{ filter: "sepia(0.15)", border: "1px solid #2D2A26", objectPosition: "top" }}
        />
      ) : (
        <EntityImageFallback kind={e.kind || e.kind_group}
                             className="w-16 h-20 shrink-0" compact />
      )}
      <span className="min-w-0 block">
        <span className="text-[14px] text-[#E4DFD3] font-heading leading-tight block">{e.name}</span>
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A68F5E] mt-1 leading-snug block">
          {e.occupation || KIND_LABEL[e.kind] || ""}
        </span>
        {e.active_range ? (
          <span className="font-mono text-[9.5px] text-[#7B7168] mt-1 block">{e.active_range}</span>
        ) : (e.first_year || e.last_year) && (
          <span className="font-mono text-[9.5px] text-[#7B7168] mt-1 block">
            {e.first_year}{e.last_year && e.last_year !== e.first_year ? ` — ${e.last_year}` : ""}
          </span>
        )}
      </span>
    </span>
    {e.summary && (
      <span className="text-[12px] leading-relaxed text-[#B9B2A4] mt-3 block">{e.summary}</span>
    )}
    <span className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: "#2D2A26" }}>
      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#7B7168]">
        {e.cameo_count} dossier{e.cameo_count === 1 ? "" : "s"}{e.connections ? ` · ${e.connections} connections` : ""}
      </span>
      <Link
        to={pathFor(e)}
        className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#D92D20] hover:underline"
        data-testid={`mini-card-open-profile-${e.slug}`}
      >
        Open {e.has_profile ? "Full Profile" : "File"} →
      </Link>
    </span>
  </span>
);

const EntityChip = ({ e }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <div
      className="relative"
      onMouseEnter={() => { clearTimeout(timer.current); setOpen(true); }}
      onMouseLeave={() => { timer.current = setTimeout(() => setOpen(false), 160); }}
    >
      {open && <MiniCard e={e} />}
      <button
        type="button"
        onClick={() => (open ? navigate(pathFor(e)) : setOpen(true))}
        className="w-full text-left border p-3 hover:border-[#D92D20] transition-colors group"
        style={{ borderColor: "#2D2A26", background: "#0d0c0a" }}
        data-testid={`dossier-entity-${e.slug}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-3 min-w-0">
            <RecordThumb targetId={e.slug}
                         className="w-10 h-10 object-cover object-top border border-[#3A3630] shrink-0"
                         fallback={null} />
            <div className="min-w-0">
              <div className="text-[14px] text-[#E4DFD3] font-heading leading-tight group-hover:text-[#D92D20] transition-colors">
                {e.name}
              </div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A39E93] mt-1">
              {e.occupation || KIND_LABEL[e.kind] || ""}
              {e.has_profile && <span className="text-[#A68F5E]"> · Profile on file</span>}
            </div>
            </div>
          </div>
          <ArrowUpRight size={12} className="text-[#454038] group-hover:text-[#D92D20] shrink-0 mt-1 transition-colors" />
        </div>
      </button>
    </div>
  );
};

const Group = ({ label, icon: Icon, items, testid }) => {
  if (!items?.length) return null;
  return (
    <section className="mt-12" data-testid={testid}>
      <DossierLabel as="h2" className="mt-0">{label}</DossierLabel>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#757065] mt-2 mb-5 flex items-center gap-2">
        <Icon size={11} className="text-[#D92D20]" />
        Hover or tap for the intelligence card
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((e) => <EntityChip key={e.slug} e={e} />)}
      </div>
    </section>
  );
};

/** Inline prose link — any entity mention inside the investigation body.
 * Hover (desktop) shows the mini card; click opens the file. */
export const EntityProseLink = ({ e, children }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <span
      className="relative inline"
      onMouseEnter={() => { clearTimeout(timer.current); setOpen(true); }}
      onMouseLeave={() => { timer.current = setTimeout(() => setOpen(false), 160); }}
    >
      {open && <MiniCard e={e} />}
      <Link
        to={pathFor(e)}
        className="text-inherit transition-colors hover:text-[#D92D20]"
        style={{ borderBottom: "1px dotted #A68F5E" }}
        data-testid={`prose-entity-${e.slug}`}
      >
        {children}
      </Link>
    </span>
  );
};

/**
 * EntityConnections — Key People / Organizations / Operations for one
 * dossier, hydrated from the entity network with mini intelligence
 * cards. Falls back to the legacy flat Key Players list when the
 * network has no entities for this dossier.
 */
export const EntityConnections = ({ topic }) => {
  const groups = useTopicEntities(topic.slug);

  if (!groups) return null;
  const total = (groups.people?.length || 0) + (groups.organizations?.length || 0) + (groups.operations?.length || 0);
  if (total === 0) return <KeyPlayersSection topic={topic} />;

  return (
    <div data-testid="dossier-entity-connections">
      <Group label="Key People"    icon={User}      items={groups.people}        testid="dossier-key-players-section" />
      <Group label="Organizations" icon={Building2} items={groups.organizations} testid="dossier-organizations-section" />
      <Group label="Operations"    icon={Crosshair} items={groups.operations}    testid="dossier-operations-section" />
    </div>
  );
};

export default EntityConnections;
